import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Loader2, MapPin, Phone, Package, Truck, CheckCircle,
  Clock, LogOut, RefreshCw, Navigation, User, Shirt,
  ArrowUpFromLine, ArrowDownToLine, ChevronRight, CalendarDays,
  Building2, Home, ListTodo, ShoppingCart, Search, Plus, Minus,
  Trash2, Tag, Zap, Sparkles, Footprints, Repeat, X, Percent,
  Receipt, TicketPercent, CreditCard, XCircle, IndianRupee, Info, Gift
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Catalog data (same as IndividualNewOrder) ─────────────────────────────────
const LAUNDRY_SERVICES = [
  { key: 'flexfold',  label: 'FlexFold',  price: 79, unit: 'kg',  desc: 'Wash + Iron',    color: 'blue',   icon: Shirt },
  { key: 'flashfold', label: 'FlashFold', price: 99, unit: 'kg',  desc: 'Express Wash+Iron', color: 'orange', icon: Zap },
  { key: 'justpress', label: 'JustPress', price: 49, unit: 'kg',  desc: 'Ironing only',   color: 'violet', icon: Tag },
];

const DRY_CLEAN_ITEMS = [
  { group: 'Women', name: 'Kurta / Kameez', price: 99 }, { group: 'Women', name: 'Salwaar', price: 89 },
  { group: 'Women', name: 'Saree light', price: 149 }, { group: 'Women', name: 'Saree heavy', price: 249 },
  { group: 'Women', name: 'Saree silk', price: 199 }, { group: 'Women', name: 'Saree designer', price: 299 },
  { group: 'Women', name: 'Lehenga Normal', price: 349 }, { group: 'Women', name: 'Lehenga Heavy', price: 399 },
  { group: 'Women', name: 'Dress', price: 199 }, { group: 'Women', name: 'Dress heavy', price: 349 },
  { group: 'Women', name: 'Blouse Normal', price: 49 }, { group: 'Women', name: 'Suit 2pcs', price: 249 },
  { group: 'Women', name: 'Suit 3pcs', price: 299 }, { group: 'Women', name: 'Coat long', price: 199 },
  { group: 'Women', name: 'Coat short', price: 149 }, { group: 'Women', name: 'Dupatta normal', price: 49 },
  { group: 'Women', name: 'Dupatta heavy', price: 99 }, { group: 'Women', name: 'Night dress', price: 79 },
  { group: 'Men',   name: 'Shirt / T-Shirt', price: 99 }, { group: 'Men', name: 'Trouser / Jeans', price: 99 },
  { group: 'Men',   name: 'Suit 2pcs', price: 349 }, { group: 'Men', name: 'Suit 3pcs', price: 449 },
  { group: 'Men',   name: 'Jacket', price: 249 }, { group: 'Men', name: 'Blazer long', price: 299 },
  { group: 'Men',   name: 'Kurta pajama light', price: 199 }, { group: 'Men', name: 'Kurta pajama heavy', price: 299 },
  { group: 'Men',   name: 'Sweater full sleeve', price: 149 }, { group: 'Men', name: 'Sweat shirt', price: 149 },
  { group: 'Kids',  name: 'Top', price: 49 }, { group: 'Kids', name: 'Frock', price: 119 },
  { group: 'Kids',  name: 'Normal dress', price: 109 }, { group: 'Kids', name: 'Combo (shirt-pant)', price: 79 },
];

const SHOE_ITEMS = [
  { name: 'Sports Shoes', price: 199 }, { name: 'Sneaker Shoes', price: 199 },
  { name: 'Leather Shoes', price: 349 }, { name: 'Suede Shoes', price: 349 },
];

const HOME_ITEMS = [
  { name: 'Blanket Single', price: 299, unit: 'piece' }, { name: 'Blanket Double', price: 399, unit: 'piece' },
  { name: 'Blanket Divet (Single)', price: 599, unit: 'piece' }, { name: 'Blanket Divet (Double)', price: 799, unit: 'piece' },
  { name: 'Quilt (Single)', price: 399, unit: 'piece' }, { name: 'Quilt (Double)', price: 499, unit: 'piece' },
  { name: 'Curtain', price: 199, unit: 'panel' }, { name: 'Carpet', price: 35, unit: 'sqft' },
];


const COLOR = {
  blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    btn: 'bg-blue-600 hover:bg-blue-700',    border: 'border-blue-200' },
  orange:  { bg: 'bg-orange-50',  text: 'text-orange-700',  btn: 'bg-orange-500 hover:bg-orange-600', border: 'border-orange-200' },
  violet:  { bg: 'bg-violet-50',  text: 'text-violet-700',  btn: 'bg-violet-600 hover:bg-violet-700', border: 'border-violet-200' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', btn: 'bg-emerald-600 hover:bg-emerald-700', border: 'border-emerald-200' },
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   btn: 'bg-amber-500 hover:bg-amber-600',   border: 'border-amber-200' },
};

// ── AssignmentCard ────────────────────────────────────────────────────────────
const AssignmentCard = ({ assignment, onComplete, completing, onMarkCashPaid, markingPaid, onCheckout, checkoutDone }) => {
  const isPickup = assignment.assignment_type === 'pickup';
  const isPg = assignment.order_type === 'pg';
  const needsCheckout = isPickup && !isPg && !checkoutDone; // checkoutDone is per-assignment (order.id in Set)
  const order = assignment.order || {};

  const mapsUrl = order.latitude && order.longitude
    ? `https://maps.google.com/?q=${order.latitude},${order.longitude}`
    : `https://maps.google.com/?q=${encodeURIComponent(
        isPg
          ? (order.pg_address || order.pg_name || '')
          : [order.address_line1, order.address_line2, order.city, order.pincode].filter(Boolean).join(', ')
      )}`;

  const fullAddress = isPg
    ? order.pg_address || order.pg_name
    : [order.address_line1, order.address_line2, order.city, order.pincode].filter(Boolean).join(', ');

  const accent = isPickup ? 'bg-blue-600' : 'bg-emerald-600';
  const accentLight = isPickup ? 'bg-blue-50 border-blue-200' : 'bg-emerald-50 border-emerald-200';
  const accentText = isPickup ? 'text-blue-700' : 'text-emerald-700';

  return (
    <div className="bg-white rounded-[1.5rem] border border-slate-200 shadow-sm overflow-hidden">
      <div className={`${accent} px-5 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2.5 text-white">
          {isPickup ? <ArrowUpFromLine className="w-4 h-4" /> : <ArrowDownToLine className="w-4 h-4" />}
          <span className="text-sm font-black uppercase tracking-wider">
            {isPickup ? 'Pickup' : 'Delivery'} — {isPg ? 'PG Customer' : 'Individual'}
          </span>
        </div>
        <span className="text-[10px] font-bold text-white/80 bg-white/20 px-2 py-0.5 rounded-full">
          {format(new Date(assignment.scheduled_date), 'dd MMM')}
        </span>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${accentLight} border`}>
            {isPg ? <Building2 className={`w-5 h-5 ${accentText}`} /> : <User className={`w-5 h-5 ${accentText}`} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-black text-slate-800 text-base truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {order.customer_name || 'Unknown Customer'}
            </p>
            {isPg && order.pg_name && <p className="text-xs text-slate-400 font-medium">{order.pg_name}</p>}
            {order.customer_phone && (
              <a href={`tel:${order.customer_phone}`} className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-blue-600">
                <Phone className="w-3 h-3" /> {order.customer_phone}
              </a>
            )}
          </div>
        </div>
        {fullAddress && (
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-start gap-2.5 p-3 bg-slate-50 rounded-xl border border-slate-100 hover:bg-blue-50 hover:border-blue-200 transition-colors group">
            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5 group-hover:text-blue-500" />
            <span className="text-xs text-slate-600 group-hover:text-blue-700 leading-relaxed flex-1">{fullAddress}</span>
            <Navigation className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-500 flex-shrink-0 mt-0.5" />
          </a>
        )}
        {!isPg && order.pickup_date && (
          <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 rounded-xl px-3 py-2 border border-blue-100 font-semibold">
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            Pickup slot: {order.pickup_date}{order.pickup_time_slot ? ` @ ${order.pickup_time_slot}` : ''}
          </div>
        )}
        {order.special_instructions && (
          <div className="text-xs text-amber-700 bg-amber-50 rounded-xl px-3 py-2 border border-amber-100">
            📝 {order.special_instructions}
          </div>
        )}
        {assignment.notes && (
          <div className="text-xs text-violet-700 bg-violet-50 rounded-xl px-3 py-2 border border-violet-100">
            💬 {assignment.notes}
          </div>
        )}
        {/* Payment status (individual orders only) */}
        {!isPg && order.total_amount > 0 && (
          <div className="flex items-center justify-between">
            {order.payment_status === 'paid' ? (
              <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">
                <CheckCircle className="w-3.5 h-3.5" /> Paid {order.payment_method === 'cash' ? '(Cash)' : '(Easebuzz)'}
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-xs font-bold text-red-600 bg-red-50 border border-red-200 px-3 py-1.5 rounded-full">
                <XCircle className="w-3.5 h-3.5" /> ₹{order.total_amount?.toFixed(0)} Unpaid
              </span>
            )}
            {order.payment_status !== 'paid' && (
              <button
                onClick={() => onMarkCashPaid && onMarkCashPaid(order.id)}
                disabled={markingPaid === order.id}
                className="flex items-center gap-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-full disabled:opacity-60 transition-colors"
              >
                {markingPaid === order.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <IndianRupee className="w-3 h-3" />}
                Cash Paid
              </button>
            )}
          </div>
        )}
        {/* Checkout button for individual pickup assignments */}
        {isPickup && !isPg && (
          <button
            onClick={() => onCheckout && onCheckout(order.customer_phone)}
            className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-xl text-sm shadow-md shadow-orange-200 transition-all hover:-translate-y-0.5"
          >
            <ShoppingCart className="w-4 h-4" />
            {checkoutDone ? 'View / Edit Checkout' : 'Checkout (Required First)'}
          </button>
        )}
        {/* Mark as Done — blocked for individual pickups until checkout is done */}
        {needsCheckout ? (
          <div className="w-full flex items-center justify-center gap-2 bg-slate-100 text-slate-400 font-bold py-3 rounded-xl text-sm border border-slate-200 cursor-not-allowed select-none">
            <CheckCircle className="w-4 h-4" />
            Mark as Done (Complete checkout first)
          </div>
        ) : (
          <button onClick={() => onComplete(assignment.id)} disabled={completing === assignment.id}
            className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold py-3 rounded-xl text-sm shadow-md shadow-emerald-200 transition-all hover:-translate-y-0.5">
            {completing === assignment.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            Mark as Done
          </button>
        )}
      </div>
    </div>
  );
};

// ── Checkout Flow ─────────────────────────────────────────────────────────────

const KgStepper = ({ value, onChange, step = 0.5, min = 0.5 }) => {
  const [inputVal, setInputVal] = React.useState('');
  const [editing, setEditing] = React.useState(false);
  return (
    <div className="flex items-center gap-1.5">
      <button onClick={() => onChange(Math.max(min, parseFloat((value - step).toFixed(1))))}
        className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 font-bold transition-colors">
        <Minus className="w-3.5 h-3.5" />
      </button>
      {editing ? (
        <input
          autoFocus
          type="number"
          min={min}
          step={step}
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onBlur={() => {
            const parsed = parseFloat(inputVal);
            if (!isNaN(parsed) && parsed >= min) onChange(parseFloat(parsed.toFixed(1)));
            setEditing(false);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="w-16 text-center font-black text-slate-800 text-sm bg-white border border-blue-300 rounded-lg py-1 focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
      ) : (
        <button
          onClick={() => { setInputVal(String(value)); setEditing(true); }}
          className="w-16 text-center font-black text-slate-800 text-sm hover:bg-blue-50 hover:text-blue-700 rounded-lg py-1 transition-colors"
          title="Click to type exact weight"
        >
          {value} kg
        </button>
      )}
      <button onClick={() => onChange(parseFloat((value + step).toFixed(1)))}
        className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 font-bold transition-colors">
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const CountStepper = ({ value, onChange, min = 1 }) => (
  <div className="flex items-center gap-2">
    <button onClick={() => onChange(Math.max(min, value - 1))}
      className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 font-bold transition-colors">
      <Minus className="w-3.5 h-3.5" />
    </button>
    <span className="w-10 text-center font-black text-slate-800 text-sm">{value}</span>
    <button onClick={() => onChange(value + 1)}
      className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 font-bold transition-colors">
      <Plus className="w-3.5 h-3.5" />
    </button>
  </div>
);

const SimplifiedCheckoutPanel = ({ token, onDone, initialPhone = '' }) => {
  const [phone, setPhone] = useState('');
  const [searching, setSearching] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [customKgCategories, setCustomKgCategories] = useState([]); // admin-created kg-priced services (e.g. Wash & Fold)

  const buildDefaultServices = (customCats) => ({
    foldpass:     { enabled: false, total_pcs: 0, total_kg: 3.0 },
    flexfold:     { enabled: false, total_pcs: 0, total_kg: 1.0 },
    justpress:    { enabled: false, total_pcs: 0, total_kg: 1.0 },
    dry_cleaning: { enabled: false, total_pcs: 1 },
    shoe_cleaning:{ enabled: false, total_pcs: 1 },
    home_items:   { enabled: false, total_pcs: 1 },
    ...Object.fromEntries((customCats || []).map(c => [c.key, { enabled: false, total_pcs: 0, total_kg: 1.0 }])),
  });

  // services: { foldpass, flexfold, justpress, dry_cleaning, shoe_cleaning, home_items, ...custom }
  // each: { enabled: bool, total_pcs: int, total_kg: float }
  const [services, setServices] = useState(() => buildDefaultServices([]));
  const [useFoldpass, setUseFoldpass] = useState(false);
  const [foldpassKg, setFoldpassKg] = useState(3.0);
  const [couponCode, setCouponCode] = useState('');
  const [couponInput, setCouponInput] = useState('');
  const [couponInfo, setCouponInfo] = useState(null);
  const [eligibleCoupons, setEligibleCoupons] = useState([]);
  const [priceInfo, setPriceInfo] = useState(null);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState(null);
  const [apiRates, setApiRates] = useState(null);

  // Fetch dynamic rates + custom kg-priced categories on mount
  useEffect(() => {
    axios.get(`${API}/individual/service-rates`)
      .then(r => setApiRates(r.data))
      .catch(() => {});
    axios.get(`${API}/individual/service-categories`)
      .then(r => setCustomKgCategories((r.data || []).filter(c => c.service_mode === 'kg' && !c.is_system && c.is_active)))
      .catch(() => {});
  }, []);

  // Seed default state for any custom category discovered after a customer is already loaded
  useEffect(() => {
    if (!customKgCategories.length) return;
    setServices(prev => {
      const next = { ...prev };
      let changed = false;
      customKgCategories.forEach(c => {
        if (!next[c.key]) { next[c.key] = { enabled: false, total_pcs: 0, total_kg: 1.0 }; changed = true; }
      });
      return changed ? next : prev;
    });
  }, [customKgCategories]);

  // Get price for a kg-based service from API or fallback
  const getRate = (cat) => {
    if (apiRates && apiRates[cat]?.[0]) return apiRates[cat][0].price;
    const fallback = { flexfold: 79, justpress: 49 };
    return fallback[cat] || 79;
  };

  // Toggle a service on/off
  const DEFAULT_SERVICE_ENTRY = { enabled: false, total_pcs: 0, total_kg: 1.0 };

  const toggleService = (key) => {
    setServices(prev => ({ ...prev, [key]: { ...(prev[key] || DEFAULT_SERVICE_ENTRY), enabled: !(prev[key] || DEFAULT_SERVICE_ENTRY).enabled } }));
  };

  const updateService = (key, field, value) => {
    setServices(prev => ({ ...prev, [key]: { ...(prev[key] || DEFAULT_SERVICE_ENTRY), [field]: value } }));
  };

  // Compute estimated subtotal (pcs-only services = ₹0 until admin fills)
  const estimatedSubtotal = (() => {
    let s = 0;
    if (services.flexfold.enabled)  s += services.flexfold.total_kg * getRate('flexfold');
    if (services.justpress.enabled) s += services.justpress.total_kg * getRate('justpress');
    customKgCategories.forEach(c => {
      if (services[c.key]?.enabled) s += (services[c.key].total_kg || 0) * getRate(c.key);
    });
    if (useFoldpass) s += 0; // foldpass always ₹0
    return parseFloat(s.toFixed(2));
  })();

  const anyServiceEnabled = Object.values(services).some(s => s.enabled) || useFoldpass;

  // Search customer
  const handleSearch = async (phoneOverride) => {
    const searchPhone = (phoneOverride !== undefined ? phoneOverride : phone).trim();
    if (!searchPhone) { toast.error('Enter a phone number'); return; }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/delivery/search-customer?phone=${searchPhone}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerData(r.data);
      setServices(buildDefaultServices(customKgCategories));
      setUseFoldpass(false);
      setFoldpassKg(3.0);
      setCouponCode('');
      setCouponInfo(null);
      try {
        const pr = await axios.get(`${API}/delivery/price-preview?customer_phone=${searchPhone}&amount=0`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPriceInfo(pr.data);
      } catch { /* ignore */ }
    } catch (err) {
      if (err.response?.status === 404) toast.error('No customer with this phone number');
      else toast.error('Search failed');
    } finally { setSearching(false); }
  };

  useEffect(() => {
    if (initialPhone) { setPhone(initialPhone); handleSearch(initialPhone); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch eligible coupons whenever estimated subtotal changes
  useEffect(() => {
    if (!customerData || estimatedSubtotal <= 0) { setEligibleCoupons([]); return; }
    axios.get(`${API}/delivery/eligible-coupons?amount=${estimatedSubtotal}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setEligibleCoupons(r.data)).catch(() => {});
  }, [estimatedSubtotal, customerData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update delivery charge when subtotal changes
  useEffect(() => {
    if (!customerData?.customer) return;
    const afterCoupon = estimatedSubtotal - (couponInfo ? estimatedSubtotal * couponInfo.discount_pct / 100 : 0);
    const plan = customerData.active_plan;
    const fpRemaining = useFoldpass && plan ? (plan.total_kg - (plan.used_kg || 0)) : null;
    const params = new URLSearchParams({ customer_phone: customerData.customer.phone, amount: afterCoupon });
    if (useFoldpass) { params.set('is_foldpass', 'true'); if (fpRemaining !== null) params.set('foldpass_remaining', fpRemaining); }
    axios.get(`${API}/delivery/price-preview?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setPriceInfo(r.data)).catch(() => {});
  }, [estimatedSubtotal, couponInfo, useFoldpass]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyCoupon = () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    const found = eligibleCoupons.find(c => c.code === code);
    if (found) { setCouponCode(code); setCouponInfo(found); toast.success(`Coupon "${code}" applied!`); }
    else toast.error('Coupon not applicable');
  };
  const removeCoupon = () => { setCouponCode(''); setCouponInfo(null); setCouponInput(''); };

  // Referral discount preview
  const cust = customerData?.customer;
  const orderCount = customerData?.order_count ?? 1;
  const referralRewardsPending = cust?.referral_rewards_pending || 0;
  const hasPendingOrderOnly = orderCount === 1 && !!customerData?.pending_order;
  const isReferee = (orderCount === 0 || hasPendingOrderOnly) && !!cust?.referred_by_id;
  const referralDiscountPct = isReferee ? 10 : (referralRewardsPending > 0 ? 5 : 0);
  const referralDiscount = referralDiscountPct > 0 ? parseFloat((estimatedSubtotal * referralDiscountPct / 100).toFixed(2)) : 0;
  const referralType = isReferee ? 'referee_first_order' : (referralRewardsPending > 0 ? 'referrer_reward' : null);
  const couponDiscount = couponInfo ? parseFloat((estimatedSubtotal * couponInfo.discount_pct / 100).toFixed(2)) : 0;
  const deliveryCharge = priceInfo?.charge || 0;
  const finalTotal = Math.max(0, estimatedSubtotal - couponDiscount - referralDiscount) + deliveryCharge;

  const handleCheckout = async () => {
    if (!anyServiceEnabled) { toast.error('Select at least one service'); return; }
    const active_plan = customerData?.active_plan;
    if (useFoldpass && active_plan) {
      const kgLeft = active_plan.total_kg - active_plan.used_kg;
      if (foldpassKg > kgLeft) { toast.error(`FoldPass KG exceeds remaining balance (${kgLeft.toFixed(1)}kg left)`); return; }
    }

    // Build services list for API
    const servicesList = [];
    if (services.foldpass.enabled) {
      servicesList.push({ category: 'foldpass_laundry', total_pcs: services.foldpass.total_pcs || null });
    }
    if (services.flexfold.enabled) {
      servicesList.push({ category: 'flexfold', total_pcs: services.flexfold.total_pcs || null, total_kg: services.flexfold.total_kg });
    }
    if (services.justpress.enabled) {
      servicesList.push({ category: 'justpress', total_pcs: services.justpress.total_pcs || null, total_kg: services.justpress.total_kg });
    }
    if (services.dry_cleaning.enabled) {
      servicesList.push({ category: 'dry_cleaning', total_pcs: services.dry_cleaning.total_pcs });
    }
    if (services.shoe_cleaning.enabled) {
      servicesList.push({ category: 'shoe_cleaning', total_pcs: services.shoe_cleaning.total_pcs });
    }
    if (services.home_items.enabled) {
      servicesList.push({ category: 'home_items', total_pcs: services.home_items.total_pcs });
    }
    customKgCategories.forEach(c => {
      const sv = services[c.key];
      if (sv?.enabled) {
        servicesList.push({ category: c.key, total_pcs: sv.total_pcs || null, total_kg: sv.total_kg });
      }
    });

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/delivery/simple-checkout`, {
        customer_phone: customerData.customer.phone,
        schedule_order_id: customerData.pending_order?.id || null,
        services: servicesList,
        special_instructions: specialInstructions || null,
        coupon_code: couponCode || null,
        use_foldpass: useFoldpass,
        foldpass_kg: useFoldpass && foldpassKg > 0 ? foldpassKg : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Pickup recorded!');
      setCheckoutResult({ orderId: res.data.order.id, total: res.data.pricing.total, scheduleOrderId: customerData.pending_order?.id });
      setCustomerData(null);
      setPhone('');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Checkout failed');
    } finally { setSubmitting(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (checkoutResult) {
    return (
      <div className="space-y-4">
        <div className="bg-white border border-slate-200/80 rounded-[2rem] p-6 shadow-sm">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-7 h-7 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-base font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Pickup Recorded!</h3>
              <p className="text-[11px] text-amber-600 mt-0.5">Admin will finalise pricing during inspection</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 mb-3">
            <XCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-amber-700">Payment Pending</p>
              <p className="text-xs text-amber-600 mt-0.5">Admin will finalise pricing during inspection</p>
            </div>
          </div>
          <button onClick={() => { const sid = checkoutResult?.scheduleOrderId; setCheckoutResult(null); onDone(sid); }}
            className="w-full py-3 rounded-2xl bg-slate-800 hover:bg-slate-900 text-white font-bold text-sm transition-all">
            Done — Back to Tasks
          </button>
        </div>
      </div>
    );
  }

  if (!customerData) {
    return (
      <div className="space-y-4">
        <div className="bg-white border border-slate-200/80 rounded-[2rem] p-6 shadow-sm">
          <h3 className="text-base font-black text-slate-800 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Search Customer by Phone
          </h3>
          <p className="text-xs text-slate-400 mb-4">Enter customer's phone number to start pickup</p>
          <div className="flex gap-2">
            <input
              type="tel" placeholder="Customer phone number" value={phone}
              onChange={e => setPhone(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="flex-1 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 text-sm outline-none transition-colors"
            />
            <button onClick={handleSearch} disabled={searching}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold px-4 py-3 rounded-xl text-sm shadow-md shadow-blue-200 transition-all">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Search
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { customer, pending_order, active_plan } = customerData;

  // Service config for the simplified UI
  const SERVICE_CONFIG = [
    {
      key: 'foldpass',
      label: 'FoldPass',
      icon: Repeat,
      color: 'blue',
      desc: 'Subscription laundry plan',
      hasPcs: true,
      hasKg: false, // kg entered via foldpassKg stepper separately
      visible: !!active_plan,
    },
    {
      key: 'flexfold',
      label: 'FlexFold',
      icon: Shirt,
      color: 'blue',
      desc: `Wash + Iron — ₹${getRate('flexfold')}/kg`,
      hasPcs: true,
      hasKg: true,
    },
    {
      key: 'justpress',
      label: 'JustPress',
      icon: Tag,
      color: 'violet',
      desc: `Ironing only — ₹${getRate('justpress')}/kg`,
      hasPcs: true,
      hasKg: true,
    },
    {
      key: 'dry_cleaning',
      label: 'Dry Cleaning',
      icon: Sparkles,
      color: 'emerald',
      desc: 'Pcs count only — admin prices',
      hasPcs: true,
      hasKg: false,
    },
    {
      key: 'shoe_cleaning',
      label: 'Shoe Cleaning',
      icon: Footprints,
      color: 'amber',
      desc: 'Pcs count only — admin prices',
      hasPcs: true,
      hasKg: false,
    },
    {
      key: 'home_items',
      label: 'Home Linen',
      icon: Home,
      color: 'orange',
      desc: 'Pcs count only — admin prices',
      hasPcs: true,
      hasKg: false,
    },
    ...customKgCategories.map(c => ({
      key: c.key,
      label: c.label,
      icon: Package,
      color: 'emerald',
      desc: `${c.label} — ₹${getRate(c.key)}/kg`,
      hasPcs: true,
      hasKg: true,
    })),
  ].filter(s => s.visible !== false);

  return (
    <div className="space-y-4">
      {/* Customer info */}
      <div className="bg-white border border-slate-200/80 rounded-[2rem] p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white font-black flex-shrink-0">
              {customer.full_name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>{customer.full_name}</p>
              <p className="text-xs text-slate-400">{customer.phone}</p>
            </div>
          </div>
          <button onClick={() => setCustomerData(null)}
            className="w-8 h-8 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        {customer.address_line1 && (
          <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 rounded-xl px-3 py-2">
            <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
            <span>{[customer.address_line1, customer.address_line2, customer.city, customer.pincode].filter(Boolean).join(', ')}</span>
          </div>
        )}
        {pending_order && (
          <div className="mt-3 flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 text-xs text-amber-700 font-medium">
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            Scheduled: {pending_order.pickup_date} {pending_order.pickup_time_slot && `@ ${pending_order.pickup_time_slot}`}
          </div>
        )}
        {active_plan && (
          <div className="mt-2 flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 text-xs text-blue-700 font-medium">
            <Repeat className="w-3.5 h-3.5 flex-shrink-0" />
            FoldPass active — {(active_plan.total_kg - active_plan.used_kg).toFixed(1)} kg remaining
          </div>
        )}
        {isReferee && (
          <div className="mt-2 flex items-center gap-2 bg-violet-50 border border-violet-100 rounded-xl px-3 py-2 text-xs text-violet-700 font-medium">
            <Gift className="w-3.5 h-3.5 flex-shrink-0" />
            10% welcome discount will be auto-applied
          </div>
        )}
        {!isReferee && referralRewardsPending > 0 && (
          <div className="mt-2 flex items-center gap-2 bg-violet-50 border border-violet-100 rounded-xl px-3 py-2 text-xs text-violet-700 font-medium">
            <Gift className="w-3.5 h-3.5 flex-shrink-0" />
            5% referral reward will be auto-applied
          </div>
        )}
      </div>

      {/* Services — simplified cards */}
      <div className="bg-white border border-slate-200/80 rounded-[2rem] p-5 shadow-sm">
        <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Select Services</p>
        <div className="space-y-3">
          {SERVICE_CONFIG.map(({ key, label, icon: Icon, color: col, desc, hasPcs, hasKg }) => {
            const c = COLOR[col] || COLOR.blue;
            const sv = services[key] || { enabled: false, total_pcs: 0, total_kg: 1.0 };
            const isOn = key === 'foldpass' ? useFoldpass : sv.enabled;

            return (
              <div key={key} className={`rounded-2xl border-2 transition-all ${isOn ? `${c.border} bg-white` : 'border-slate-100 bg-slate-50/50'}`}>
                {/* Header row — tap to toggle */}
                <button
                  onClick={() => key === 'foldpass' ? setUseFoldpass(p => !p) : toggleService(key)}
                  className="w-full flex items-center gap-3 p-4"
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isOn ? c.bg : 'bg-slate-100'}`}>
                    <Icon className={`w-4 h-4 ${isOn ? c.text : 'text-slate-400'}`} />
                  </div>
                  <div className="flex-1 text-left">
                    <p className={`text-sm font-bold ${isOn ? 'text-slate-800' : 'text-slate-500'}`}>{label}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{desc}</p>
                  </div>
                  <div className={`w-11 h-6 rounded-full transition-all flex-shrink-0 ${isOn ? 'bg-blue-600' : 'bg-slate-200'}`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow-sm m-0.5 transition-all ${isOn ? 'translate-x-5' : 'translate-x-0'}`} />
                  </div>
                </button>

                {/* Inputs when enabled */}
                {isOn && (
                  <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                    {/* Pcs count */}
                    {hasPcs && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600 font-medium">
                          {key === 'foldpass' ? 'Approx. pieces' : 'Total pieces'}
                        </span>
                        <CountStepper
                          value={key === 'foldpass' ? (services.foldpass.total_pcs || 0) : (sv.total_pcs || 1)}
                          onChange={v => {
                            if (key === 'foldpass') updateService('foldpass', 'total_pcs', v);
                            else updateService(key, 'total_pcs', v);
                          }}
                          min={key === 'foldpass' ? 0 : 1}
                        />
                      </div>
                    )}
                    {/* KG input for kg-based services */}
                    {hasKg && key !== 'foldpass' && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600 font-medium">Total weight</span>
                        <KgStepper
                          value={sv.total_kg || 1.0}
                          onChange={v => updateService(key, 'total_kg', v)}
                          step={0.5} min={0.5}
                        />
                      </div>
                    )}
                    {/* FoldPass KG */}
                    {key === 'foldpass' && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600 font-medium">Laundry weight (kg)</span>
                          <KgStepper value={foldpassKg} onChange={setFoldpassKg} step={0.5} min={0.5} />
                        </div>
                        {active_plan && (() => {
                          const kgLeft = active_plan.total_kg - active_plan.used_kg;
                          const over = foldpassKg > kgLeft;
                          return (
                            <p className={`text-[11px] font-semibold ${over ? 'text-red-500' : 'text-emerald-600'}`}>
                              {over ? `⚠ Exceeds plan balance (${kgLeft.toFixed(1)}kg left)` : `${(kgLeft - foldpassKg).toFixed(1)}kg will remain`}
                            </p>
                          );
                        })()}
                      </div>
                    )}
                    {/* Estimated total for kg-based */}
                    {hasKg && key !== 'foldpass' && (
                      <p className={`text-xs font-bold ${c.text}`}>
                        Estimated: ₹{(sv.total_kg * getRate(key)).toFixed(0)}
                      </p>
                    )}
                    {/* Note for pcs-only */}
                    {!hasKg && key !== 'foldpass' && (
                      <p className="text-[10px] text-amber-600 bg-amber-50 rounded-lg px-2 py-1.5 font-medium">
                        ₹0 now — admin will price during inspection
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Special instructions */}
      <div className="bg-white border border-slate-200/80 rounded-[2rem] p-5 shadow-sm">
        <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Notes (Optional)</p>
        <input
          type="text" placeholder="Any special instructions?"
          value={specialInstructions} onChange={e => setSpecialInstructions(e.target.value)}
          className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 text-sm outline-none transition-colors"
        />
      </div>

      {/* Eligible coupons */}
      {eligibleCoupons.length > 0 && !couponInfo && estimatedSubtotal > 0 && (
        <div className="bg-white border border-slate-200/80 rounded-[2rem] p-5 shadow-sm">
          <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
            <TicketPercent className="w-3.5 h-3.5" /> Eligible Coupons
          </p>
          <div className="space-y-2">
            {eligibleCoupons.map(c => (
              <button key={c.id} onClick={() => { setCouponInput(c.code); setCouponCode(c.code); setCouponInfo(c); toast.success(`Coupon applied!`); }}
                className="w-full flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 hover:bg-emerald-100 transition-colors">
                <div className="text-left">
                  <p className="text-sm font-bold text-emerald-800">{c.code}</p>
                  <p className="text-xs text-emerald-600">{c.discount_pct}% off</p>
                </div>
                <span className="text-sm font-black text-emerald-700">{c.discount_pct}% OFF</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Coupon input */}
      {estimatedSubtotal > 0 && (
        <div className="bg-white border border-slate-200/80 rounded-[2rem] p-5 shadow-sm">
          <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
            <Percent className="w-3.5 h-3.5" /> Coupon
          </p>
          {couponInfo ? (
            <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
              <div>
                <p className="text-sm font-bold text-emerald-800">{couponInfo.code} — {couponInfo.discount_pct}% off</p>
                <p className="text-xs text-emerald-600">Saving ₹{couponDiscount.toFixed(2)}</p>
              </div>
              <button onClick={removeCoupon} className="w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input type="text" placeholder="Coupon code" value={couponInput}
                onChange={e => setCouponInput(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && applyCoupon()}
                className="flex-1 px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm outline-none uppercase font-bold" />
              <button onClick={applyCoupon} className="bg-emerald-600 text-white font-bold px-4 py-2.5 rounded-xl text-sm">Apply</button>
            </div>
          )}
        </div>
      )}

      {/* Summary + Checkout */}
      <div className="bg-white border border-slate-200/80 rounded-[2rem] p-5 shadow-sm">
        <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
          <Receipt className="w-3.5 h-3.5" /> Pickup Summary
        </p>
        {anyServiceEnabled ? (
          <div className="space-y-2 text-sm mb-4">
            {/* Services list — just what was picked up, no prices */}
            {SERVICE_CONFIG.map(({ key, label }) => {
              const isOn = key === 'foldpass' ? useFoldpass : services[key].enabled;
              if (!isOn) return null;
              const sv = services[key];
              const pcs = key === 'foldpass' ? (sv.total_pcs || 0) : (sv.total_pcs || 1);
              const kgStr = key === 'flexfold' ? ` · ${sv.total_kg}kg` : key === 'justpress' ? ` · ${sv.total_kg}kg` : key === 'foldpass' ? ` · ${foldpassKg}kg` : '';
              const isPcsOnly = !['flexfold', 'justpress', 'foldpass'].includes(key);
              return (
                <div key={key} className="flex items-center justify-between py-1 border-b border-slate-50 last:border-0">
                  <span className="text-slate-700">{label}{pcs > 0 ? ` — ${pcs} pcs` : ''}{kgStr}</span>
                  {isPcsOnly && (
                    <span className="text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full font-semibold">Priced later</span>
                  )}
                </div>
              );
            })}
            <div className="pt-2 border-t border-slate-100">
              <p className="text-[11px] text-slate-400">📦 Orders below ₹300 — ₹6/km delivery charge applies</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400 mb-4">No services selected yet</p>
        )}
        <button
          onClick={handleCheckout}
          disabled={submitting || !anyServiceEnabled}
          className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black py-4 rounded-2xl shadow-lg shadow-emerald-200 transition-all hover:-translate-y-0.5"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
          {submitting ? 'Recording...' : 'Confirm Pickup'}
        </button>
      </div>
    </div>
  );
};

// ── DeliveryDashboard ─────────────────────────────────────────────────────────
const DeliveryDashboard = () => {
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('today');
  const [completing, setCompleting] = useState(null);
  const [checkoutDone, setCheckoutDone] = useState(0);
  const [checkoutKey, setCheckoutKey] = useState(0); // increments on every checkout launch to force remount
  const [markingPaid, setMarkingPaid] = useState(null);
  const [checkoutInitialPhone, setCheckoutInitialPhone] = useState('');
  const [checkoutDoneOrders, setCheckoutDoneOrders] = useState(new Set()); // schedule order ids

  const fetchDashboard = useCallback(async () => {
    if (!token) { navigate('/login'); return; }
    try {
      const res = await axios.get(`${API}/delivery/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        logout(); navigate('/login');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally { setLoading(false); }
  }, [token, navigate, logout]);

  useEffect(() => {
    fetchDashboard();
    const iv = setInterval(fetchDashboard, 60000);
    return () => clearInterval(iv);
  }, [fetchDashboard]);

  const handleComplete = async (assignmentId) => {
    setCompleting(assignmentId);
    try {
      await axios.patch(`${API}/delivery/assignments/${assignmentId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marked as done!');
      await fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark complete');
    } finally { setCompleting(null); }
  };

  const handleMarkCashPaid = async (orderId) => {
    setMarkingPaid(orderId);
    try {
      await axios.patch(`${API}/delivery/orders/${orderId}/payment`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marked as paid by cash');
      await fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as paid');
    } finally { setMarkingPaid(null); }
  };

  const handleStartCheckout = (phone) => {
    setCheckoutInitialPhone(phone || '');
    setCheckoutKey(k => k + 1); // force CheckoutPanel to remount with fresh state
    setTab('checkout');
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-3">
        <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200 animate-pulse">
          <img src="/justfold-logo-transparent.png" alt="" className="w-7 h-7" style={{ filter: 'brightness(0) invert(1)' }} />
        </div>
        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
      </div>
    </div>
  );

  const todayTasks = data?.today_assignments || [];
  const upcomingTasks = data?.upcoming_assignments || [];
  const completedToday = data?.completed_today || 0;

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <header className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-emerald-600 px-5 pt-6 pb-8 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/10 rounded-full" />
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
              <img src="/logo.png" alt="JustFold" className="h-8 w-auto object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
            </button>
            <div className="flex items-center gap-2">
              <button onClick={fetchDashboard} className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center text-white hover:bg-white/30 transition-colors">
                <RefreshCw className="w-4 h-4" />
              </button>
              <button onClick={handleLogout} className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center text-white hover:bg-white/30 transition-colors">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
          <p className="text-blue-200 text-sm font-medium mb-0.5">{greeting()},</p>
          <h1 className="text-2xl font-black text-white mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {data?.delivery_boy_name?.split(' ')[0] || 'Delivery'}! 🚴
          </h1>
          <p className="text-blue-200 text-sm">{format(new Date(), 'EEEE, dd MMMM yyyy')}</p>
          <div className="flex gap-3 mt-4">
            {[
              { label: 'Today', value: todayTasks.length },
              { label: 'Done Today', value: completedToday },
              { label: 'Upcoming', value: upcomingTasks.length },
            ].map(({ label, value }) => (
              <div key={label} className="flex-1 bg-white/15 rounded-2xl px-3 py-2.5 text-center backdrop-blur-sm">
                <p className="text-xl font-black text-white">{value}</p>
                <p className="text-[10px] text-blue-200 font-semibold uppercase tracking-wide">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex gap-2 px-4 py-4 overflow-x-auto no-scrollbar">
        {[
          { key: 'today',    label: `Today (${todayTasks.length})`,       icon: ListTodo },
          { key: 'upcoming', label: `Upcoming (${upcomingTasks.length})`, icon: CalendarDays },
          { key: 'checkout', label: 'Checkout',                           icon: ShoppingCart },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex-1 min-w-max flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-sm font-bold transition-all ${
              tab === key
                ? key === 'checkout'
                  ? 'bg-emerald-600 text-white shadow-md shadow-emerald-200'
                  : 'bg-blue-600 text-white shadow-md shadow-blue-200'
                : 'bg-white text-slate-500 border border-slate-200 hover:border-blue-200'
            }`}>
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="px-4 pb-8 space-y-4">
        {tab === 'today' && (
          todayTasks.length === 0 ? (
            <div className="bg-white rounded-[2rem] border border-slate-200 py-16 flex flex-col items-center gap-3 text-center shadow-sm">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-emerald-400" strokeWidth={1.5} />
              </div>
              <p className="text-lg font-bold text-slate-600">All done for today!</p>
              <p className="text-sm text-slate-400">No pending assignments for today</p>
              {completedToday > 0 && <p className="text-sm font-semibold text-emerald-600">✓ {completedToday} completed today</p>}
            </div>
          ) : (
            todayTasks.map(a => <AssignmentCard key={a.id} assignment={a} onComplete={handleComplete} completing={completing} onMarkCashPaid={handleMarkCashPaid} markingPaid={markingPaid} onCheckout={handleStartCheckout} checkoutDone={checkoutDoneOrders.has(a.order?.id)} />)
          )
        )}
        {tab === 'upcoming' && (
          upcomingTasks.length === 0 ? (
            <div className="bg-white rounded-[2rem] border border-slate-200 py-16 flex flex-col items-center gap-3 text-center shadow-sm">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center">
                <CalendarDays className="w-8 h-8 text-slate-300" strokeWidth={1.5} />
              </div>
              <p className="text-lg font-bold text-slate-600">No upcoming assignments</p>
            </div>
          ) : (
            upcomingTasks.map(a => <AssignmentCard key={a.id} assignment={a} onComplete={handleComplete} completing={completing} onMarkCashPaid={handleMarkCashPaid} markingPaid={markingPaid} onCheckout={handleStartCheckout} checkoutDone={checkoutDoneOrders.has(a.order?.id)} />)
          )
        )}
        {tab === 'checkout' && (
          <SimplifiedCheckoutPanel
            key={checkoutKey}
            token={token}
            initialPhone={checkoutInitialPhone}
            onDone={(scheduleOrderId) => {
              setCheckoutDone(p => p + 1);
              if (scheduleOrderId) {
                setCheckoutDoneOrders(prev => new Set([...prev, scheduleOrderId]));
              }
              setCheckoutInitialPhone('');
              setTab('today');
              fetchDashboard();
            }}
          />
        )}

      </div>
    </div>
  );
};

export default DeliveryDashboard;
